import { z } from "zod";

/** Validation message bag — supplied by callers from the `Validation`
 *  next-intl namespace so all copy stays out of this module. */
export type ValidationMessages = {
  phoneInvalid: string;
  phoneChars: string;
};

function phoneSchema(msg: ValidationMessages) {
  return z
    .string()
    .min(5, msg.phoneInvalid)
    .max(40)
    .regex(/^[+0-9 ()/-]+$/, msg.phoneChars);
}

export const quoteStep1Schema = z.object({
  fromZip: z.string().min(2).max(12),
  fromCity: z.string().min(2).max(80),
  fromCountry: z.string().min(2).max(2),
  toZip: z.string().min(2).max(12),
  toCity: z.string().min(2).max(80),
  toCountry: z.string().min(2).max(2),
});

export const quoteStep2Schema = z.object({
  kind: z.enum(["letter", "parcel", "pallet", "machine", "other"]),
  weightKg: z.coerce.number().min(0).max(50_000),
  lengthCm: z.coerce.number().min(0).max(2_000).optional(),
  widthCm: z.coerce.number().min(0).max(500).optional(),
  heightCm: z.coerce.number().min(0).max(500).optional(),
  when: z.enum(["now", "today", "scheduled"]),
  scheduledAt: z.string().optional(),
});

/** Factory: builds the full quote schema with localized phone messages. */
export function makeQuoteSchema(msg: ValidationMessages) {
  const quoteStep3Schema = z.object({
    company: z.string().min(2).max(120),
    name: z.string().min(2).max(120),
    email: z.string().email(),
    phone: phoneSchema(msg),
    callMeNow: z.boolean().default(false),
  });
  return quoteStep1Schema.merge(quoteStep2Schema).merge(quoteStep3Schema);
}

/** Factory: callback form schema. */
export function makeCallbackSchema(msg: ValidationMessages) {
  return z.object({ phone: phoneSchema(msg) });
}

/** Factory: contact form schema. */
export function makeContactSchema(msg: ValidationMessages) {
  return z.object({
    name: z.string().min(2).max(120),
    company: z.string().max(120).optional(),
    email: z.string().email(),
    phone: phoneSchema(msg).optional(),
    topic: z.enum(["quote", "service", "partner", "press", "other"]).default("quote"),
    message: z.string().min(10).max(4_000),
  });
}

/**
 * Factory: carrier (transport partner) registration — the `/transport-partner`
 * driver-apply lead. Reuses the shared `phoneSchema`. The three confirmation
 * checkboxes must be explicitly checked (`.refine(v => v === true)`).
 */
export function makeCarrierRegistrationSchema(msg: {
  phoneInvalid: string;
  phoneChars: string;
}) {
  return z.object({
    company: z.string().min(2).max(160),
    // Any ISO country code — the booking flow's CountrySelect returns these.
    country: z.string().min(2).max(2).default("DE"),
    ustId: z.string().max(40).optional(),
    gewerbeConfirm: z.boolean().refine((v) => v === true),
    salutation: z.enum(["mr", "ms", "diverse"]).optional(),
    firstName: z.string().min(2).max(80),
    lastName: z.string().min(2).max(80),
    email: z.string().email(),
    phone: phoneSchema(msg),
    street: z.string().min(2).max(160),
    zip: z.string().min(3).max(12),
    city: z.string().min(2).max(80),
    // Fleet: a count per selected vehicle type (was a single global count).
    fleet: z
      .array(
        z.object({
          type: z.enum(["pkw", "sprinter", "lkw75", "lkw12", "lkw40"]),
          count: z.number().int().min(1).max(9999),
        }),
      )
      .min(1),
    region: z.string().min(2).max(160),
    // Uploaded proof documents — public Bunny CDN URLs from /api/carrier-upload.
    // Optional so registration never hard-blocks if storage is briefly
    // unavailable; the insurance-confirmation checkbox stays required.
    documents: z
      .object({
        euLicense: z.string().url().optional(),
        insurance: z.string().url().optional(),
        trade: z.string().url().optional(),
      })
      .optional(),
    insuranceConfirm: z.boolean().refine((v) => v === true),
    privacyConsent: z.boolean().refine((v) => v === true),
  });
}

// Stable type exports — inferred from the factory output with a fixed
// (empty-message) schema so the shape doesn't depend on the message bag.
const _EMPTY: ValidationMessages = { phoneInvalid: "", phoneChars: "" };
export type QuoteData = z.infer<ReturnType<typeof makeQuoteSchema>>;
export type CallbackData = z.infer<ReturnType<typeof makeCallbackSchema>>;
export type ContactData = z.infer<ReturnType<typeof makeContactSchema>>;
export type CarrierRegistrationData = z.infer<
  ReturnType<typeof makeCarrierRegistrationSchema>
>;
void _EMPTY;
